import type { App } from 'vue'
import type { Configuration } from '@swipe4work/api-client-fetch'
import { EmployerApi, GeneralApi } from '@swipe4work/api-client-fetch'
import { apiClientFetchConfiguration, coordinatorClientConfig } from '@/configs/client-configs'
import { Configuration as CoordinatorConfiguration, PortalUserApi, UserApi } from '@swipe4work/ai-coordinator-client'

export type EmployerClientType = EmployerApi
export type GeneralClientType = GeneralApi
export type CoordinatorUserClientType = UserApi
export type CoordinatorPortalClientType = PortalUserApi
export type ApiClientTypes = EmployerClientType | GeneralClientType | CoordinatorUserClientType | CoordinatorPortalClientType

export interface ApiClients {
  employerApi: EmployerClientType
  generalApi: GeneralClientType
  coordinatorUserApi: CoordinatorUserClientType
  coordinatorPortalApi: CoordinatorPortalClientType
}

const employerApiInjectionKey = Symbol('employerApi')
const generalApiInjectionKey = Symbol('generalApi')
const coordinatorUserApiInjectionKey = Symbol('coordinatorUserApi')
const coordinatorPortalApiInjectionKey = Symbol('coordinatorPortalApi')

export function useApiClients(): ApiClients {
  return {
    employerApi: useEmployerApiClient(),
    generalApi: useGeneralApiClient(),
    coordinatorUserApi: useCoordinatorUserClient(),
    coordinatorPortalApi: useCoordinatorPortalClient(),
  }
}

export function useEmployerApiClient() {
  const employerApi = inject(employerApiInjectionKey) as (config: Configuration) => EmployerApi

  return employerApi(apiClientFetchConfiguration(useToast()))
}

export function useGeneralApiClient() {
  const generalApi = inject(generalApiInjectionKey) as (config: Configuration) => GeneralApi

  return generalApi(apiClientFetchConfiguration(useToast()))
}

export function useCoordinatorUserClient() {
  const coordinatorUserApi = inject(coordinatorUserApiInjectionKey) as (config: CoordinatorConfiguration) => UserApi

  return coordinatorUserApi(coordinatorClientConfig())
}

export function useCoordinatorPortalClient() {
  const coordinatorPortalApi = inject(coordinatorPortalApiInjectionKey) as (config: CoordinatorConfiguration) => PortalUserApi

  return coordinatorPortalApi(coordinatorClientConfig())
}

export default {
  install(app: App, options?: {
    accessTokenOverride?: string,
    basePathOverride?: string 
  }): void {
    app.provide(
      employerApiInjectionKey,
      () => new EmployerApi(apiClientFetchConfiguration(useToast(), options?.basePathOverride, options?.accessTokenOverride)),
    )
    app.provide(
      generalApiInjectionKey,
      () => new GeneralApi(apiClientFetchConfiguration(useToast(), options?.basePathOverride, options?.accessTokenOverride)),
    )
    app.provide(
      coordinatorUserApiInjectionKey,
      (config: CoordinatorConfiguration) => new UserApi(config),
    )
    app.provide(
      coordinatorPortalApiInjectionKey,
      (config: CoordinatorConfiguration) => new PortalUserApi(config),
    )
  },
}
